import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contactsApi } from '@/api/contacts';
import { Contact, ContactCreateUpdateRequest, PaginatedContactList, ContactLimits } from '@/types/api';
import { toast } from 'sonner';

// Query keys
export const contactKeys = {
  all: ['contacts'] as const,
  lists: () => [...contactKeys.all, 'list'] as const,
  list: (params?: any) => [...contactKeys.lists(), params] as const,
  details: () => [...contactKeys.all, 'detail'] as const,
  detail: (id: number) => [...contactKeys.details(), id] as const,
  birthdays: (days?: number) => [...contactKeys.all, 'birthdays', days] as const,
  byRelationship: (type: string) => [...contactKeys.all, 'by-relationship', type] as const,
  relationshipTypes: () => [...contactKeys.all, 'relationship-types'] as const,
  search: (query: string) => [...contactKeys.all, 'search', query] as const,
  limits: () => [...contactKeys.all, 'limits'] as const,
};

// Hook to get contacts list
export const useContacts = (params?: {
  page?: number;
  page_size?: number;
  search?: string;
  ordering?: string;
}, queryOptions?: any) => {
  return useQuery({
    queryKey: contactKeys.list(params),
    queryFn: () => contactsApi.list(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    ...queryOptions, // Allow overriding default options
  });
};

// Hook to get a single contact
export const useContact = (id: number) => {
  return useQuery({
    queryKey: contactKeys.detail(id),
    queryFn: () => contactsApi.get(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Hook to create a contact
export const useCreateContact = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: ContactCreateUpdateRequest) => contactsApi.create(data),
    onSuccess: (data) => {
      // Invalidate and refetch contacts list
      queryClient.invalidateQueries({ queryKey: contactKeys.lists() });
      // Invalidate contact limits as the count has changed
      queryClient.invalidateQueries({ queryKey: contactKeys.limits() });
    },
    onError: (error) => {
      const errorMessage = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Failed to create contact';
      toast.error(errorMessage);
    },
  });
};

// Hook to update a contact
export const useUpdateContact = (id: number) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: ContactCreateUpdateRequest) => contactsApi.update(id, data),
    onSuccess: (data) => {
      // Update the cached contact data
      queryClient.setQueryData(contactKeys.detail(id), data);
      // Invalidate contacts list
      queryClient.invalidateQueries({ queryKey: contactKeys.lists() });
      toast.success('Contact updated successfully');
    },
    onError: (error) => {
      const errorMessage = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Failed to update contact';
      toast.error(errorMessage);
    },
  });
};

// Hook to partially update a contact
export const usePartialUpdateContact = (id: number) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: Partial<ContactCreateUpdateRequest>) => contactsApi.partialUpdate(id, data),
    retry: false, // Disable automatic retries
    onSuccess: (data) => {
      // Update the cached contact data
      queryClient.setQueryData(contactKeys.detail(id), data);
      // Instead of invalidating, update the contact in the list cache
      queryClient.setQueriesData(
        { queryKey: contactKeys.lists() },
        (oldData: any) => {
          if (!oldData?.results) return oldData;
          return {
            ...oldData,
            results: oldData.results.map((contact: Contact) =>
              contact.id === id ? data : contact
            ),
          };
        }
      );
    },
    onError: (error) => {
      // Don't show toast here, let the component handle it
      console.error('Contact update error:', error);
    },
  });
};

// Hook to delete a contact
export const useDeleteContact = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: number) => contactsApi.delete(id),
    onSuccess: (_, id) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: contactKeys.detail(id) });
      // Invalidate contacts list
      queryClient.invalidateQueries({ queryKey: contactKeys.lists() });
      // Invalidate contact limits as the count has changed
      queryClient.invalidateQueries({ queryKey: contactKeys.limits() });
      toast.success('Contact deleted successfully');
    },
    onError: (error) => {
      const errorMessage = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Failed to delete contact';
      toast.error(errorMessage);
    },
  });
};

// Hook to get relationship types
export const useRelationshipTypes = () => {
  return useQuery({
    queryKey: contactKeys.relationshipTypes(),
    queryFn: () => contactsApi.getRelationshipTypes(),
    staleTime: 30 * 60 * 1000, // 30 minutes (this data rarely changes)
    gcTime: 60 * 60 * 1000, // 1 hour
    retry: 1, // Only retry once on failure
  });
};

// Hook to search contacts
export const useSearchContacts = (query: string, enabled = true) => {
  return useQuery({
    queryKey: contactKeys.search(query),
    queryFn: () => contactsApi.search(query),
    enabled: enabled && !!query,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Hook to get contacts with upcoming birthdays
export const useContactBirthdays = (days?: number) => {
  return useQuery({
    queryKey: contactKeys.birthdays(days),
    queryFn: () => contactsApi.getBirthdays(days),
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  });
};

// Hook to get contacts by relationship type
export const useContactsByRelationship = (type: string, enabled = true) => {
  return useQuery({
    queryKey: contactKeys.byRelationship(type),
    queryFn: () => contactsApi.getByRelationship(type),
    enabled: enabled && !!type,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Hook to get contact limits
export const useContactLimits = () => {
  return useQuery({
    queryKey: contactKeys.limits(),
    queryFn: () => contactsApi.getLimits(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};